import React, { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import DetectOutsideClick from '../DetectOutsideClick/DetectOutsideClick';
import Icon from '../Icon/Icon';
import HelperText from '../HelperText/HelperText';
import { useTheme } from '../../../../../ThemeContext';

import styles from './PickerField.module.scss';

type Props = {
  label?: ReactNode;
  required?: boolean;
  value?: string;
  placeholder?: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  popoverWidth?: number;
  /** When true, the value area becomes an editable text input */
  editable?: boolean;
  /** Called when the user commits a typed value (on blur or Enter) */
  onValueChange?: (value: string) => void;
  children: (close: () => void) => ReactNode;
};

const POPOVER_HEIGHT = 320;

const PickerField: React.FC<Props> = ({ label, required, value, placeholder, icon, error, helperText, disabled, popoverWidth, editable, onValueChange, children }: Props) => {
  const { universalStyles } = useTheme();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; openUpward: boolean }>({ top: 0, left: 0, width: 0, openUpward: false });
  const triggerRef = useRef<HTMLDivElement>(null);
  const iconBtnRef = useRef<HTMLButtonElement>(null);
  const [inputValue, setInputValue] = useState(value || '');

  // Keep local input in sync when external value changes (e.g. from dropdown selection)
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const close = () => setOpen(false);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current || iconBtnRef.current;
    if (!el) { return; }
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < POPOVER_HEIGHT + 16 && rect.top > spaceBelow;
    const width = popoverWidth || rect.width;
    let left = rect.left;
    // Keep popover within the viewport horizontally
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    setCoords({
      top: openUpward ? rect.top - 8 : rect.bottom + 8,
      left,
      width,
      openUpward,
    });
  }, [popoverWidth]);

  useLayoutEffect(() => {
    if (open) { updatePosition(); }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) { return; }
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  const handleInputCommit = () => {
    if (onValueChange && inputValue !== value) {
      onValueChange(inputValue);
    }
  };

  return (
    <div className={classNames(styles.field, { [styles.error]: error })}>
      {label && (
        <div className={styles.label}>
          {label}
          {required ? (
            <span className={styles.required} style={universalStyles.errorColor}>
              *
            </span>
          ) : null}
        </div>
      )}
      <div className={styles.wrapper}>
        {editable ? (
          <div
            ref={triggerRef}
            className={classNames(styles.control, styles.editableControl, { [styles.open]: open, [styles.disabled]: disabled })}
          >
            <input
              type="text"
              className={classNames(styles.value, styles.editableInput)}
              value={inputValue}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleInputCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleInputCommit();
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            <button
              ref={iconBtnRef}
              type="button"
              className={styles.iconBtn}
              onClick={() => !disabled && setOpen((prev) => !prev)}
              disabled={disabled}
              tabIndex={-1}
              aria-label="Open time picker"
            >
              <span className={styles.icon} aria-hidden>
                <Icon icon={icon} />
              </span>
            </button>
          </div>
        ) : (
          <button
            ref={iconBtnRef as unknown as React.RefObject<HTMLButtonElement>}
            type="button"
            className={classNames(styles.control, { [styles.open]: open, [styles.disabled]: disabled })}
            onClick={() => !disabled && setOpen((prev) => !prev)}
            disabled={disabled}
          >
            <span className={classNames(styles.value, { [styles.placeholder]: !value })}>{value || placeholder}</span>
            <span className={styles.icon} aria-hidden>
              <Icon icon={icon} />
            </span>
          </button>
        )}

        {open &&
          ReactDOM.createPortal(
            <DetectOutsideClick callback={close}>
              <div
                className={styles.popover}
                style={{
                  position: 'fixed',
                  top: coords.top,
                  left: coords.left,
                  minWidth: coords.width,
                  transform: coords.openUpward ? 'translateY(-100%)' : undefined,
                  zIndex: 10005,
                }}
              >
                {children(close)}
              </div>
            </DetectOutsideClick>,
            document.body,
          )}
      </div>
      <HelperText error={error}>{helperText}</HelperText>
    </div>
  );
};

export default PickerField;
